import { styled } from '@mui/material/styles';
import { Box, FormHelperText, TextField } from '@mui/material';

import { Themes, DEFAULT_INPUT_HEIGHT } from '../../constants';
import {
  FORTA_BLACK,
  FORTA_WHITE,
  FORTA_ERROR,
  FORTA_BLUE,
  FORTA_SUCCESS,
} from '../../utils/colors';
import { getThemeColor } from '../../utils/common';

import type { FormHelperTextProps, TextFieldProps as MTextFieldProps } from '@mui/material';
import type { ReactNode } from 'react';

type TextFieldProps = MTextFieldProps & {
  colorTheme?: Themes;
  coloredBackground?: boolean;
  inputHeight?: number;
  error?: boolean;
  infoText?: ReactNode;
};

const StyledInputField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'inputHeight' &&
    prop !== 'error',
})<TextFieldProps>(({ colorTheme, coloredBackground, inputHeight, error, sx = {} }) => ({
  backgroundColor: coloredBackground
    ? getThemeColor(colorTheme ?? Themes.Primary).light3
    : FORTA_WHITE,
  borderRadius: '8px',
  overflow: 'hidden',
  minWidth: 150,
  border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
  '&:focus-within': {
    border: error ? `2px solid ${FORTA_ERROR}` : `2px solid ${FORTA_SUCCESS}`,
  },

  '& .MuiInputBase-root': {
    color: FORTA_BLACK,
    '&.Mui-disabled': {
      color: FORTA_BLACK,
      backgroundColor: coloredBackground
        ? getThemeColor(colorTheme ?? Themes.Primary).light3
        : FORTA_WHITE,
    },

    '& .MuiOutlinedInput-input': {
      height: inputHeight,
      boxSizing: 'border-box',
      padding: '0.5em',

      '&.Mui-disabled': {
        WebkitTextFillColor: FORTA_BLACK,
        cursor: 'not-allowed',
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '&.MuiFilledInput-root': {
      background: 'transparent',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },
    ...sx,
  },
}));

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'fullHeight',
})<FormHelperTextProps>(({ error }: any) => ({
  color:
    error === null || error === undefined
      ? `${FORTA_BLUE.primary}`
      : error === false
        ? `${FORTA_SUCCESS}`
        : `${FORTA_ERROR}`,
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '21px',
}));

const FHInputField = (props: TextFieldProps) => {
  const {
    colorTheme = Themes.Primary,
    coloredBackground = false,
    inputHeight = DEFAULT_INPUT_HEIGHT,
    error = false,
  } = props;
  const { helperText, infoText, ...inputProps } = props;
  const { sx } = inputProps;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
      <StyledInputField
        colorTheme={colorTheme}
        coloredBackground={coloredBackground}
        inputHeight={inputHeight}
        error={error}
        {...inputProps}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: infoText ? 'space-between' : 'end',
          alignItems: 'center',
        }}
      >
        {infoText}
        <StyledFormHelperText error={error}>
          <span>{helperText}</span>
        </StyledFormHelperText>
      </Box>
    </Box>
  );
};

export default FHInputField;
